import { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import WizardStateContext from 'components/Wizards/shared/context/wizardstate';
import { REPORT_TYPES } from 'components/Wizards/Dashlet/types';
import {
  StyledStepHeader,
  StyledWizardField,
} from 'components/Wizards/shared/styles';
import MultiSelect from 'components/Inputs/MultiSelect';
import { KizenTypography } from '__app/typography';

import { useActivitiesQuery } from 'queries/models/activites';

import useMetadataKey from 'components/Wizards/shared/hooks/useMetadataKey';
import { IconWithTooltip } from 'pages/Common/components/LinkWithIconTooltip/IconWithTooltip';
import styled from '@emotion/styled';

const IconWithTooltipStyled = styled(IconWithTooltip)`
  padding: 0 0 10px 0;
  align: base-line;
  margin-left: 5px;
  color: var(--color-background-dark);
  :hover {
    color: var(--color-background-darker);
  }
`;

export const ScheduledActivitiesCalendarReportType = () => {
  const { t } = useTranslation();
  const { state, setState } = useContext(WizardStateContext.Context);
  const { reportType, activities, hasDeletedActivities } = state;
  const selectRef = useRef(null);

  const { data, isLoading } = useActivitiesQuery({
    select: (results) => {
      return results.map((activity) => {
        return {
          label: activity.name,
          value: activity.id,
        };
      });
    },
  });

  const activitiesValue = useMemo(() => {
    return (
      activities?.map(({ label, value }) => {
        if (!isLoading && !data?.find((item) => item.value === value)) {
          return {
            label: t('[Deleted] - {{label}}', { label }),
            value,
            deleted: true,
          };
        }
        return { label, value };
      }) ?? []
    );
  }, [activities, t, data, isLoading]);

  useMetadataKey(
    'hasDeletedActivities',
    activitiesValue.some((item) => item.deleted)
  );

  const activitiesOnChange = useCallback(
    (value) => {
      setState('activities', value);
    },
    [setState]
  );

  if (reportType?.value !== REPORT_TYPES.SCHEDULED_ACTIVITIES_CALENDAR) {
    return null;
  }

  return (
    <StyledWizardField top={12}>
      <StyledStepHeader>
        <KizenTypography type="subheader">
          {t('Choose Activities to Display (Leave Blank for All)')}
        </KizenTypography>
        <IconWithTooltipStyled
          label={t(
            'Only selected activities will be available for scheduling from this dashlet'
          )}
          icon="info-circle"
          color="var(--color-button-tertiary-hover)"
        />
      </StyledStepHeader>
      <MultiSelect
        ref={selectRef}
        isLoading={isLoading}
        onChange={activitiesOnChange}
        placeholder={t('Find Activities')}
        className="modal-multiselect activity-types"
        options={data ?? []}
        value={activitiesValue}
        error={hasDeletedActivities}
        data-qa-id="scheduled-activities-calendar-multiselect"
      />
    </StyledWizardField>
  );
};
